import React, { useState, useEffect, useCallback } from 'react';
import { Gate } from '../AccessStudio';

// Per-child end-of-day report. Meals are grouped by type (Breakfast/Lunch/Snack)
// and marked with a visual amount picker; plus additional food, care toggles,
// potty/diaper, feeling, needs, notes. See DAYCARE_DIARY_DESIGN.md.

// Ordinal amounts render as a dot-meter (a "how much eaten" picture); the two
// special amounts render as an icon.
const AMOUNTS = [
  { key: 'None', label: 'None', dots: 0 },
  { key: 'Some', label: 'Some', dots: 1 },
  { key: 'Most', label: 'Most', dots: 2 },
  { key: 'ThreeQuarters', label: '¾', dots: 3 },
  { key: 'All', label: 'All', dots: 4 },
  { key: 'Double', label: 'Double', dots: 5 },
  { key: 'Tupperware', label: 'Box', icon: '🥡' },
  { key: 'Others', label: 'Other', icon: '❓' },
];
const MEAL_TYPES = [
  { key: 'Breakfast', label: 'Breakfast', icon: '🍳' },
  { key: 'Lunch', label: 'Lunch', icon: '🍽️' },
  { key: 'Snack', label: 'Snack', icon: '🍎' },
  { key: 'Other', label: 'Other', icon: '➕' },
];
const FEELINGS = [
  { key: 'Happy', label: '😊 Happy' }, { key: 'VeryExcited', label: '🤩 Very Excited' },
  { key: 'Dynamic', label: '⚡ Dynamic' }, { key: 'Brave', label: '💪 Brave' }, { key: 'BadHumor', label: '😖 Bad Humor' },
];

const Dots = ({ n }) => (
  <span className="tracking-tight" aria-hidden>
    {[0, 1, 2, 3, 4].map((i) => (
      <span key={i} className={i < n ? 'text-amber-500' : 'text-gray-300'}>●</span>
    ))}
  </span>
);

const AmountPicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1">
    {AMOUNTS.map((a) => {
      const active = value === a.key;
      return (
        <button key={a.key} type="button" title={a.label} onClick={() => onChange(active ? null : a.key)}
          className={`flex flex-col items-center justify-center w-14 h-11 rounded border text-[10px] leading-tight ${active ? 'bg-amber-500 text-white border-amber-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
          <span className="text-sm">{a.icon || <Dots n={a.dots} />}</span>
          <span>{a.label}</span>
        </button>
      );
    })}
  </div>
);

const YesNo = ({ icon, label, value, onChange }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-sm text-gray-700">{icon} {label}</span>
    <div className="flex gap-1">
      {[['Yes', true], ['No', false]].map(([t, v]) => (
        <button key={t} type="button" onClick={() => onChange(value === v ? null : v)}
          className={`px-3 py-1 rounded text-sm border ${value === v ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600'}`}>{t}</button>
      ))}
    </div>
  </div>
);

const CountRow = ({ label, value, onChange }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-sm text-gray-700">{label}</span>
    <div className="flex gap-1">
      {[0, 1, 2, 3].map((n) => (
        <button key={n} type="button" onClick={() => onChange(value === n ? null : n)}
          className={`w-8 h-8 rounded text-sm border ${value === n ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600'}`}>{n}</button>
      ))}
    </div>
  </div>
);

const DaycareChildReportModal = ({ isOpen, onClose, onSuccess, child, date, apiCall, showError, showSuccess }) => {
  const [menu, setMenu] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [meals, setMeals] = useState({});
  const [needIds, setNeedIds] = useState([]);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const blank = {
    waterRefill: null, nap: null, milk: null, temperature: null, feeling: null,
    pottyPoo: null, pottyPee: null, diaperPoo: null, diaperPee: null, additionalFood: '', notes: '',
  };

  const load = useCallback(async () => {
    if (!child || !date) return;
    setLoading(true);
    try {
      const [menuData, needsData, rep] = await Promise.all([
        apiCall(`/daycare/daily-report/menu?classId=${child.ClassID}&date=${date}`),
        apiCall('/daycare/daily-report/needs'),
        apiCall(`/daycare/daily-report/child?childId=${child.ChildID}&date=${date}`),
      ]);
      setMenu(menuData || []);
      setNeeds(needsData || []);
      const mealMap = {};
      (rep.meals || []).forEach((m) => { mealMap[m.MenuItemID] = m.Amount; });
      setMeals(mealMap);
      setNeedIds(rep.needIds || []);
      const r = rep.report;
      setForm(r ? {
        waterRefill: r.WaterRefill, nap: r.Nap, milk: r.Milk, temperature: r.Temperature, feeling: r.Feeling,
        pottyPoo: r.PottyPoo, pottyPee: r.PottyPee, diaperPoo: r.DiaperPoo, diaperPee: r.DiaperPee,
        additionalFood: r.AdditionalFood || '', notes: r.Notes || '',
      } : { ...blank });
    } catch (e) {
      showError('Error loading report: ' + e.message);
    } finally { setLoading(false); }
  }, [child, date, apiCall, showError]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (thenSend) => {
    setSaving(true);
    try {
      await apiCall('/daycare/daily-report/child', {
        method: 'PUT',
        body: JSON.stringify({
          childId: child.ChildID, date, ...form,
          meals: Object.entries(meals).map(([menuItemId, amount]) => ({ menuItemId: Number(menuItemId), amount })),
          needIds,
        }),
      });
      if (thenSend) {
        await apiCall('/daycare/daily-report/child/send', { method: 'POST', body: JSON.stringify({ childId: child.ChildID, date }) });
      }
      showSuccess(`Report saved${thenSend ? ' & sent' : ''} for ${child.FirstName}`);
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      showError('Error saving report: ' + e.message);
    } finally { setSaving(false); }
  };

  if (!isOpen || !child) return null;

  // Group menu items by meal type, keeping type order then untyped last.
  const groups = MEAL_TYPES.map((t) => ({ ...t, items: menu.filter((m) => m.MealType === t.key) }))
    .concat([{ key: '_none', label: 'Meals', icon: '🍽️', items: menu.filter((m) => !m.MealType) }])
    .filter((g) => g.items.length);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Daily Report — {child.FirstName} {child.LastName}</h2>
            <p className="text-amber-100 text-sm">{date}</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl leading-none">&times;</button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : (
          <div className="p-6 space-y-5 overflow-y-auto">
            {/* Meals grouped by type */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">🍽️ Meals</h3>
              {menu.length === 0 ? (
                <p className="text-sm text-gray-500">No menu set for this class/day. Add it via “Edit Menu”.</p>
              ) : (
                <div className="space-y-4">
                  {groups.map((g) => (
                    <div key={g.key}>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{g.icon} {g.label}</div>
                      <div className="space-y-2">
                        {g.items.map((item) => (
                          <div key={item.MenuItemID} className="flex items-center justify-between gap-3 flex-wrap">
                            <span className="text-sm text-gray-700 flex-1 min-w-[120px]">{item.ItemName}</span>
                            <AmountPicker value={meals[item.MenuItemID] || null}
                              onChange={(v) => setMeals((m) => { const n = { ...m }; if (v) n[item.MenuItemID] = v; else delete n[item.MenuItemID]; return n; })} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Additional food */}
              <div className="mt-3">
                <label className="block text-sm text-gray-700 mb-1">➕ Additional food (brought / extra)</label>
                <input value={form.additionalFood || ''} onChange={(e) => set('additionalFood', e.target.value)}
                  placeholder="e.g. yogurt from home, extra fruit" className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
            </div>

            {/* Care toggles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 border-t pt-4">
              <YesNo icon="💧" label="Water refill" value={form.waterRefill} onChange={(v) => set('waterRefill', v)} />
              <YesNo icon="😴" label="Nap" value={form.nap} onChange={(v) => set('nap', v)} />
              <YesNo icon="🍼" label="Milk" value={form.milk} onChange={(v) => set('milk', v)} />
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-gray-700">🌡️ Temperature</span>
                <div className="flex gap-1">
                  {[['< 37.5', 'Under375'], ['> 37.5', 'Over375']].map(([t, v]) => (
                    <button key={v} type="button" onClick={() => set('temperature', form.temperature === v ? null : v)}
                      className={`px-3 py-1 rounded text-sm border ${form.temperature === v ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600'}`}>{t}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Potty / diaper */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 border-t pt-4">
              <CountRow label="🚽 Potty Poo" value={form.pottyPoo} onChange={(v) => set('pottyPoo', v)} />
              <CountRow label="🚽 Potty Pee" value={form.pottyPee} onChange={(v) => set('pottyPee', v)} />
              <CountRow label="🧷 Diaper Poo" value={form.diaperPoo} onChange={(v) => set('diaperPoo', v)} />
              <CountRow label="🧷 Diaper Pee" value={form.diaperPee} onChange={(v) => set('diaperPee', v)} />
            </div>

            {/* Feeling */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-800 mb-2">Feeling</h3>
              <div className="flex flex-wrap gap-2">
                {FEELINGS.map((fl) => (
                  <button key={fl.key} type="button" onClick={() => set('feeling', form.feeling === fl.key ? null : fl.key)}
                    className={`px-3 py-1.5 rounded-full text-sm border ${form.feeling === fl.key ? 'bg-amber-500 text-white border-amber-500' : 'border-gray-300 text-gray-600'}`}>{fl.label}</button>
                ))}
              </div>
            </div>

            {/* Needs */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-800 mb-2">🎒 Needs (bring tomorrow)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {needs.map((n) => (
                  <label key={n.NeedID} className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={needIds.includes(n.NeedID)}
                      onChange={(e) => setNeedIds((ids) => e.target.checked ? [...ids, n.NeedID] : ids.filter((x) => x !== n.NeedID))} />
                    {n.LabelEn} <span className="text-gray-400">/ {n.LabelFr}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="border-t pt-4">
              <label className="block font-semibold text-gray-800 mb-1">📝 Notes</label>
              <textarea value={form.notes || ''} onChange={(e) => set('notes', e.target.value)}
                rows={3} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <Gate permission="daycare.daily_report.edit" label="Save child report">
            <button onClick={() => save(false)} disabled={saving || loading} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300">{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={() => save(true)} disabled={saving || loading} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300">Save &amp; Send</button>
          </Gate>
        </div>
      </div>
    </div>
  );
};

export default DaycareChildReportModal;
